import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import useResizeObserver from "use-resize-observer";

export function useOverflowList(itemCount: number, recalculate = false) {
	/** State */
	const containerRef = useRef<HTMLDivElement>(null);
	const [visibleCount, setVisibleCount] = useState(itemCount);

	/** Functions */
	const recalculateFn = useCallback(() => {
		const container = containerRef.current;
		if (!container) return;

		const containerWidth = container.offsetWidth;
		const measureRow = container.querySelector<HTMLElement>("[data-overflow-measure]");
		if (!measureRow) return;

		const itemEls = Array.from(measureRow.querySelectorAll<HTMLElement>("[data-measure-item]"));
		const indicatorEl = measureRow.querySelector<HTMLElement>("[data-measure-overflow]");
		const indicatorWidth = indicatorEl?.offsetWidth ?? 0;

		let count = 0;

		for (let i = 0; i < itemEls.length; i++) {
			const el = itemEls[i];
			if (!el) break;
			const rightEdge = el.offsetLeft + el.offsetWidth;
			const isLast = i === itemEls.length - 1;

			if (isLast) {
				if (rightEdge <= containerWidth) {
					count = i + 1;
				}
			} else {
				const nextEl = itemEls[i + 1];
				const gapAfter = nextEl ? nextEl.offsetLeft - rightEdge : 0;
				if (rightEdge + gapAfter + indicatorWidth <= containerWidth) {
					count = i + 1;
				} else {
					break;
				}
			}
		}

		setVisibleCount(count);
	}, []);

	/** Effects */
	useResizeObserver({
		ref: containerRef as RefObject<HTMLDivElement>,
		onResize: recalculate ? recalculateFn : undefined,
	});

	useEffect(() => {
		recalculateFn();
	}, [itemCount, recalculateFn]);

	/** Return */
	return {
		containerRef,
		visibleCount,
		hiddenCount: itemCount - visibleCount,
		recalculate: recalculateFn,
	};
}

export type OverflowListState = ReturnType<typeof useOverflowList>;
